import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from '../lib/icons';
import type { Tone } from '../lib/format';
import { HelpButton } from './Help';

export function Button({
  variant = 'secondary',
  size,
  icon,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'small'; icon?: IconName }) {
  return (
    <button type="button" className={`btn ${variant}${size ? ` ${size}` : ''}${className ? ` ${className}` : ''}`} {...rest}>
      {icon && <Icon name={icon} size={size === 'small' ? 15 : 17} strokeWidth={2.2} />}
      {children}
    </button>
  );
}

export function Chip({ tone = 'neutral', small, children }: { tone?: Tone; small?: boolean; children: ReactNode }) {
  return <span className={`chip ${tone}${small ? ' small' : ''}`}>{children}</span>;
}

export function Card({ title, action, children, foot, id }: { title?: ReactNode; action?: ReactNode; children: ReactNode; foot?: ReactNode; id?: string }) {
  const headId = useId();
  return (
    <section className="card" aria-labelledby={title ? headId : undefined} id={id}>
      {title && (
        <div className="card-head">
          <h2 id={headId}>{title}</h2>
          {action}
        </div>
      )}
      {children}
      {foot && <div className="card-foot">{foot}</div>}
    </section>
  );
}

export function Kpi({ label, value, foot, small, to }: { label: string; value: ReactNode; foot?: ReactNode; small?: boolean; to?: string }) {
  const body = (
    <>
      <span className="kpi-label">{label}</span>
      <span className={`kpi-value${small ? ' small' : ''}`}>{value}</span>
      {foot && <span className="kpi-foot">{foot}</span>}
    </>
  );
  return to ? (
    <Link className="kpi" to={to}>
      {body}
    </Link>
  ) : (
    <div className="kpi">{body}</div>
  );
}

export function PageHeader({ eyebrow, title, chips, actions, help = true }: { eyebrow?: ReactNode; title: string; chips?: ReactNode; actions?: ReactNode; help?: boolean }) {
  useEffect(() => {
    document.title = `${title} · Stitch`;
  }, [title]);
  return (
    <header className="topbar">
      <div className="topbar-title">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h1 className="page-title">{title}</h1>
          {chips}
        </div>
      </div>
      {(actions || help) && (
        <div className="topbar-actions">
          {actions}
          {/* Every screen: its own guide, and a way to ask Stitch. */}
          {help && <HelpButton />}
        </div>
      )}
    </header>
  );
}

export function Notice({ tone = 'neutral', title, children, icon }: { tone?: Tone; title?: ReactNode; children?: ReactNode; icon?: IconName }) {
  const name: IconName = icon ?? (tone === 'ok' ? 'check' : tone === 'neutral' || tone === 'brand' ? 'info' : 'alert');
  return (
    <div className={`notice ${tone}`} role={tone === 'crit' ? 'alert' : 'status'}>
      <Icon name={name} size={18} strokeWidth={2.1} />
      <div className="notice-body">
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children && <span>{children}</span>}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}…
    </div>
  );
}

export function LoadError({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <div className="card-body">
      <Notice tone="crit" title="Couldn't load this">
        {error.message}{' '}
        {retry && (
          <button type="button" className="link-btn" onClick={retry}>
            Try again
          </button>
        )}
      </Notice>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode; error?: string }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        aria-invalid={error ? true : undefined}
        aria-describedby={[hint ? hintId : '', error ? errId : ''].filter(Boolean).join(' ') || undefined}
        {...input}
      />
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error" id={errId}>
          {error}
        </span>
      )}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  error,
  options,
  ...select
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hint?: ReactNode; error?: string; options: readonly { value: string; label: string; group?: string }[] }) {
  const id = useId();
  const option = (o: { value: string; label: string }) => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  );
  // Options with a group show under it, groups in the order they first come.
  const groups = [...new Set(options.map((o) => o.group ?? ''))];
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="input" aria-invalid={error ? true : undefined} aria-describedby={hint ? `${id}-hint` : undefined} {...select}>
        {groups.length > 1 || groups[0]
          ? groups.map((g) =>
              g ? (
                <optgroup key={g} label={g}>
                  {options.filter((o) => o.group === g).map(option)}
                </optgroup>
              ) : (
                options.filter((o) => !o.group).map(option)
              )
            )
          : options.map(option)}
      </select>
      {hint && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  ...area
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: ReactNode; error?: string }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} className="input textarea" aria-invalid={error ? true : undefined} aria-describedby={hint ? `${id}-hint` : undefined} {...area} />
      {hint && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

/** Shared behaviour for dialogs and drawers: focus, Escape, scroll lock, focus return. */
export function useOverlay(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // A field marked data-autofocus wins; otherwise the first field (querySelector with a list
    // would return whichever comes first in the page, not the first in the list).
    const first =
      panel.current?.querySelector<HTMLElement>('[data-autofocus]') ?? panel.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
    (first ?? panel.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      // With a dialog open over another (a sub-menu over its menu), keys belong to the top one.
      const scrims = document.querySelectorAll('.scrim');
      if (scrims.length && scrims[scrims.length - 1] !== panel.current?.parentElement) return;
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab' && panel.current) {
        const items = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])')];
        const firstEl = items[0];
        const lastEl = items[items.length - 1];
        if (!firstEl || !lastEl) return;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open]);
  return panel;
}

interface OverlayProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
}

export function Drawer({ open, onClose, title, subtitle, children, foot }: OverlayProps) {
  const panel = useOverlay(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="drawer" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panel} tabIndex={-1}>
        <OverlayHead titleId={titleId} title={title} subtitle={subtitle} onClose={onClose} />
        <div className="overlay-body">{children}</div>
        {foot && <div className="overlay-foot">{foot}</div>}
      </div>
    </div>
  );
}

/** A phone's bottom sheet (the menu, your account): a dialog's behaviour, coming up from the bottom. */
export function Sheet({ open, onClose, title, subtitle, children }: Omit<OverlayProps, 'foot'>) {
  const panel = useOverlay(open, onClose);
  const titleId = useId();
  if (!open) return null;
  return (
    <div className="scrim sheet-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panel} tabIndex={-1}>
        <OverlayHead titleId={titleId} title={title} subtitle={subtitle} onClose={onClose} />
        <div className="overlay-body">{children}</div>
      </div>
    </div>
  );
}

/** Dialogs that start a change by hand: Leland offers to do it instead (see LelandLauncher). */
const EDITING = /^(Add|Edit|Change|Remove|Quick add|Move|Give)\b/;

export function Dialog({ open, onClose, title, subtitle, children, foot }: OverlayProps) {
  const panel = useOverlay(open, onClose);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    if (typeof title === 'string' && EDITING.test(title)) window.dispatchEvent(new CustomEvent('stitch:editing', { detail: { title } }));
    const close = () => closeRef.current();
    window.addEventListener('stitch:close-overlays', close);
    return () => window.removeEventListener('stitch:close-overlays', close);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panel} tabIndex={-1}>
        <OverlayHead titleId={titleId} title={title} subtitle={subtitle} onClose={onClose} />
        <div className="overlay-body">{children}</div>
        {foot && <div className="overlay-foot">{foot}</div>}
      </div>
    </div>
  );
}

function OverlayHead({ titleId, title, subtitle, onClose }: { titleId: string; title: ReactNode; subtitle?: ReactNode; onClose: () => void }) {
  return (
    <div className="overlay-head">
      <div className="grow">
        <h2 id={titleId}>{title}</h2>
        {subtitle && <span className="muted">{subtitle}</span>}
      </div>
      <button type="button" className="icon-btn" onClick={onClose} aria-label="Close" data-close>
        <Icon name="x" size={17} strokeWidth={2.2} />
      </button>
    </div>
  );
}

/**
 * A short message at the bottom of the screen after a quick action ("Dana is logged out"), with
 * an optional action such as Undo. Goes away by itself.
 */
export function Toast({
  message,
  tone = 'neutral',
  action,
  onDismiss,
  timeoutMs = 9000
}: {
  message: ReactNode;
  tone?: Tone;
  action?: { label: string; run: () => void };
  onDismiss: () => void;
  timeoutMs?: number;
}) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    const t = window.setTimeout(() => dismiss.current(), timeoutMs);
    return () => window.clearTimeout(t);
  }, [message, timeoutMs]);
  return (
    <div className={`toast ${tone}`} role="status" aria-live="polite">
      <span className="grow">{message}</span>
      {action && (
        <button type="button" className="toast-action" onClick={action.run}>
          {action.label}
        </button>
      )}
      <button type="button" className="icon-btn" aria-label="Dismiss" onClick={onDismiss}>
        <Icon name="x" size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
}
